import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Backdrop,
  Paper,
  Slider,
  Button,
  useTheme,
} from '@mui/material';
import { TimeSelection } from '../model/types';
import { Stack } from '@mui/system';

// ── Clock face SVG ────────────────────────────────────────────────────────────
// 12 pie slices arranged in a circle. Dragging clockwise increases hours,
// counter-clockwise decreases. Scroll wheel also works.

const CX = 54;
const CY = 54;
const R = 40;
const SLICES = 12;

function slicePath(i: number): string {
  const from = ((i * 360) / SLICES) * (Math.PI / 180);
  const to = (((i + 1) * 360) / SLICES) * (Math.PI / 180);
  const x1 = CX + R * Math.cos(from);
  const y1 = CY + R * Math.sin(from);
  const x2 = CX + R * Math.cos(to);
  const y2 = CY + R * Math.sin(to);
  return `M${CX},${CY} L${x1},${y1} A${R},${R} 0 0,1 ${x2},${y2}z`;
}

function ClockDial({
  hours,
  onChange,
}: {
  hours: number;
  onChange: (h: number) => void;
}) {
  const theme = useTheme();
  const lastSliceRef = useRef<number | null>(null);

  // Dragging over a slice: detect direction of movement and adjust hours
  const handlePointerOver = useCallback(
    (i: number) => {
      const last = lastSliceRef.current;
      if (last === null) { lastSliceRef.current = i; return; }
      if (last === i) return;

      const diff = last - i;
      // Adjacent clockwise: last=2, i=3 → diff=-1; wrap: last=11, i=0 → diff=11
      let delta = 0;
      if (diff === 1 || diff === -(SLICES - 1)) delta = -1; // counter-clockwise
      if (diff === -1 || diff === (SLICES - 1)) delta = 1;  // clockwise

      if (delta !== 0) {
        const next = Math.max(0, hours + delta);
        onChange(next);
      }
      lastSliceRef.current = i;
    },
    [hours, onChange],
  );

  const handlePointerLeave = () => { lastSliceRef.current = null; };

  // Whether slice i should be filled
  const filled = (i: number): boolean => {
    const isPM = Math.floor(hours / SLICES) % 2 !== 0;
    const mod = hours % SLICES;
    return isPM ? mod <= i : mod > i;
  };

  const fillColor = theme.palette.primary.main;
  const emptyColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const strokeColor = theme.palette.divider;
  const ringColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : '#e0e0e0';
  const dotColor = theme.palette.text.primary;
  const markerColor = theme.palette.text.disabled;

  return (
    <svg
      viewBox="0 0 108 108"
      width={120}
      height={120}
      style={{ cursor: 'pointer', flexShrink: 0 }}
      onPointerLeave={handlePointerLeave}
    >
      {/* Outer ring */}
      <circle cx={CX} cy={CY} r={R + 6} fill="none" stroke={ringColor} strokeWidth={2} />
      {/* Clock face: border */}
      <circle cx={CX} cy={CY} r={R} fill="none" stroke={strokeColor} strokeWidth={0.5} />

      {Array.from({ length: SLICES }, (_, i) => (
        <path
          key={i}
          d={slicePath(i)}
          fill={filled(i) ? fillColor : emptyColor}
          stroke={strokeColor}
          strokeWidth={0.5}
          onPointerOver={() => handlePointerOver(i)}
          style={{ pointerEvents: 'all' }}
        />
      ))}

      {/* Hour markers */}
      {Array.from({ length: SLICES }, (_, i) => {
        const angle = ((i * 360) / SLICES - 90) * (Math.PI / 180);
        const mx = CX + (R + 3) * Math.cos(angle);
        const my = CY + (R + 3) * Math.sin(angle);
        return (
          <circle key={`marker-${i}`} cx={mx} cy={my} r={1.5}
            fill={i === 0 ? dotColor : markerColor} />
        );
      })}

      {/* Centre dot */}
      <circle cx={CX} cy={CY} r={3} fill={dotColor} />
    </svg>
  );
}

// ── TimePicker modal ──────────────────────────────────────────────────────────

function TimePicker({
  time,
  setTime,
  submit,
  showClock,
  toggleClock,
  parentRef,
}: {
  time: TimeSelection;
  setTime: (time: TimeSelection) => void;
  submit: () => void;
  showClock: boolean;
  toggleClock: () => void;
  parentRef: React.RefObject<HTMLDivElement>;
}) {
  const [days, setDays] = useState(time.days || 0);
  const [hours, setHours] = useState(time.hours || 1);
  const [minutes, setMinutes] = useState(time.minutes || 0);

  // Sync parent time object while modal is open
  useEffect(() => {
    if (showClock) setTime({ days, hours, minutes });
  }, [days, hours, minutes, setTime, showClock]);

  // Re-initialise from parent when modal opens
  useEffect(() => {
    if (showClock) {
      setDays(time.days || 0);
      setHours(time.hours || 1);
      setMinutes(time.minutes || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClock]);

  // Scroll wheel on clock changes hours
  const handleScroll = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1 : -1;
      setHours((h) => Math.max(0, h + delta));
    },
    [],
  );

  const handleSubmit = () => {
    submit();
    toggleClock();
  };

  const isEmpty = days === 0 && hours === 0 && minutes === 0;

  const label = [
    days > 0 ? `${days}d` : '',
    hours > 0 ? `${hours}h` : '',
    minutes > 0 ? `${minutes}m` : '',
  ].filter(Boolean).join(' ') || '0m';

  return (
    <>
      {showClock && (
        <Backdrop
          // Must sit above AppShell topbar (z=1100) and tabbar (z=1100),
          // otherwise the topbar crops the top of the modal and the tabbar
          // crops its footer buttons. Matches MUI's theme.zIndex.modal.
          sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal }}
          open={showClock}
          onClick={toggleClock}
        >
          <Paper
            elevation={6}
            sx={{
              p: 3,
              borderRadius: 3,
              minWidth: 300,
              maxWidth: '90vw',
            }}
            onClick={(e) => e.stopPropagation()}
            onWheel={handleScroll}
          >
            <Stack spacing={2.5}>
              <Typography variant="h5" textAlign="center" sx={{ letterSpacing: 2 }}>
                Set duration
              </Typography>

              {/* Clock + current value */}
              <Stack direction="row" spacing={3} alignItems="center" justifyContent="center">
                <ClockDial hours={hours} onChange={setHours} />
                <Stack spacing={0.5} alignItems="center">
                  <Typography
                    sx={{
                      fontFamily: '"VT323", monospace',
                      fontSize: '2.8rem',
                      lineHeight: 1,
                      letterSpacing: 2,
                      color: isEmpty ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    drag or scroll ·<br />adjust below
                  </Typography>
                </Stack>
              </Stack>

              {/* Days slider */}
              <Box sx={{ px: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Days</Typography>
                  <Typography variant="body2" fontWeight={600}>{days}</Typography>
                </Stack>
                <Slider
                  value={days}
                  onChange={(_, v) => setDays(v as number)}
                  min={0} max={30}
                  size="small"
                />
              </Box>

              {/* Hours slider (mirrors clock) */}
              <Box sx={{ px: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Hours</Typography>
                  <Typography variant="body2" fontWeight={600}>{hours}</Typography>
                </Stack>
                <Slider
                  value={hours}
                  onChange={(_, v) => { setHours(v as number); }}
                  min={0} max={23}
                  size="small"
                />
              </Box>

              {/* Minutes slider */}
              <Box sx={{ px: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Minutes</Typography>
                  <Typography variant="body2" fontWeight={600}>{minutes}</Typography>
                </Stack>
                <Slider
                  value={minutes}
                  onChange={(_, v) => setMinutes(v as number)}
                  min={0} max={59} step={5}
                  size="small"
                />
              </Box>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={toggleClock}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isEmpty}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Backdrop>
      )}
    </>
  );
}

export default TimePicker;
